import { useTranslation } from 'react-i18next'
import {
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@itsrever/design-system'
import { clsx } from 'clsx'
import { CompensationLabel } from '@/ui/components/process-details/compensation-label'
import { ProductPreview, RestockRow } from '@/ui/components/process-details'
import { ItemStatus } from '@/ui/components/items-status'
import { useAppSelector } from '@/redux/hooks'
import { RestockItem, RestockLocation } from '@/domain/restock'
import { RefundMethod } from '@/domain/refund'
import { ReturnedItem } from '@/domain/returns'
import ReviewButtons from './review-buttons'
import { NewReview } from '@/domain/review'

interface ProductCardProps {
  index: number
  lineItem: ReturnedItem
  userSelectedPm: RefundMethod
  reviewMode: boolean
  reviews: NewReview[]
  setReviews: (reviews: NewReview[]) => void
  restockMode: boolean
  newRestocks: RestockItem[]
  setNewRestocks: (restocks: RestockItem[]) => void
  restockLocations?: Map<string, RestockLocation[]>
}

export const ProductCard: React.FC<ProductCardProps> = ({
  index: i,
  lineItem,
  userSelectedPm,
  reviewMode,
  reviews,
  setReviews,
  restockMode,
  newRestocks,
  setNewRestocks,
  restockLocations
}) => {
  const { t } = useTranslation()

  const settings = useAppSelector((s) => s.settingsApi.settings.response)
  const restockAllowed = settings.restocking_available

  const itemRestockLocations = restockLocations?.get(lineItem.product.variantId)
    ? (restockLocations.get(lineItem.product.variantId) ?? [])
    : []

  const isRestocked = newRestocks[i]?.status === 'RESTOCKED'
  const canRestock = !isRestocked && restockAllowed
  const showRestockRow =
    canRestock &&
    (reviewMode || (restockMode && itemRestockLocations.length > 0))

  const isSelectedForRestock = reviewMode
    ? reviews[i]?.shouldRestock
    : newRestocks[i]?.status === 'SHOULD_RESTOCK'

  function updateStock() {
    if (reviewMode) {
      const newReviews = [...reviews]
      newReviews[i].shouldRestock = !newReviews[i].shouldRestock
      setReviews(newReviews)
      return
    }
    if (restockMode) {
      const tmpRestocks = [...newRestocks]
      if (tmpRestocks[i].status === 'SHOULD_RESTOCK') {
        tmpRestocks[i].status = 'NOT_RESTOCKED'
      } else tmpRestocks[i].status = 'SHOULD_RESTOCK'
      setNewRestocks(tmpRestocks)
      return
    }
  }

  const handleClickCard = () => {
    if (restockMode && !isRestocked) {
      updateStock()
    }
  }

  const onChangeRestock = (shouldRestock: boolean) => {
    if (reviewMode) {
      const newReviews = [...reviews]
      newReviews[i].shouldRestock = shouldRestock

      setReviews(newReviews)
    }

    const tmpRestocks = [...newRestocks]
    tmpRestocks[i].status = shouldRestock ? 'SHOULD_RESTOCK' : 'NOT_RESTOCKED'
    setNewRestocks(tmpRestocks)
  }

  const onChangeLocation = (location?: RestockLocation) => {
    if (reviewMode) {
      const newReviews = [...reviews]
      newReviews[i].location = location
      setReviews(newReviews)
    }

    const tmpRestocks = [...newRestocks]
    tmpRestocks[i].location = location
    setNewRestocks(tmpRestocks)
  }

  const showReasonTooltip =
    reviews[i]?.rejectReason?.length &&
    (reviews[i]?.rejectReason?.length ?? 0) > 35

  return (
    <div
      onClick={handleClickCard}
      className={clsx(
        'overflow-hidden rounded-md border border-solid border-[#E1E4EAFF]',
        isSelectedForRestock &&
          restockMode &&
          'outline outline-1 -outline-offset-1 outline-[#335CFFFF]/50',
        restockMode && !isRestocked && 'cursor-pointer hover:shadow-md',
        isRestocked && restockMode && 'opacity-70'
      )}
    >
      <div
        className={clsx(
          'flex min-h-12 w-full items-center justify-between py-2 pl-4 pr-2',
          'border-b border-solid border-[#E1E4EAFF] bg-[#F5F7FAFF]'
        )}
      >
        <div className="flex items-center gap-2">
          {restockMode && !isRestocked ? (
            <Checkbox checked={isSelectedForRestock} />
          ) : null}
          <CompensationLabel
            refundMethod={userSelectedPm}
            action={lineItem.action}
          />
        </div>
        {lineItem.review ? (
          <div className="flex flex-col items-end gap-1">
            <ItemStatus status={lineItem.review} />
            {reviews[i].rejectReason ? (
              <Tooltip>
                <TooltipTrigger>
                  <p className="max-w-64 truncate text-xs font-light">
                    <span className="text-xs text-slate-800">
                      {t('titles_li.reason')}:{' '}
                    </span>
                    <span className="text-xs text-slate-500">
                      {reviews[i]?.rejectReason}
                    </span>
                  </p>
                </TooltipTrigger>
                {showReasonTooltip ? (
                  <TooltipContent>
                    <p className="max-w-64 text-sm font-light">
                      {reviews[i].rejectReason}
                    </p>
                  </TooltipContent>
                ) : null}
              </Tooltip>
            ) : null}
          </div>
        ) : null}

        {reviewMode ? (
          <ReviewButtons reviews={reviews} setReviews={setReviews} index={i} />
        ) : null}
      </div>
      <div className="p-3">
        <div className="flex">
          <ProductPreview lineItem={lineItem} restocked={isRestocked} />
        </div>
        {showRestockRow ? (
          <RestockRow
            isRestockOnlyMode={restockMode}
            restock={newRestocks[i]}
            restockLocations={itemRestockLocations}
            onChangeRestock={onChangeRestock}
            onChangeLocation={onChangeLocation}
          />
        ) : null}
      </div>
    </div>
  )
}
